import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';

/**
 * Tiny coordinator that ensures only ONE prompt plays at a time across the
 * whole module. Cards register themselves by a stable id and the store
 * exposes `nowPlaying` so other rows know to stop their <audio> elements.
 */
interface AudioState {
  nowPlaying: string | null;
  setNowPlaying: (id: string | null) => void;
}

const useStore = create<AudioState>((set) => ({
  nowPlaying: null,
  setNowPlaying: (id) => set({ nowPlaying: id }),
}));

/**
 * usePromptAudio(id, url)
 *
 * Returns play/pause state, current progress, duration, and a toggle
 * handler. The audio element is created eagerly on mount with
 * `preload="metadata"` so the duration is known before the user ever
 * clicks play — metadata requests are lightweight (range-byte headers),
 * not full audio downloads. When another row starts playing this row's
 * audio is paused automatically.
 */
export function usePromptAudio(id: string, url: string | undefined) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const nowPlaying = useStore((s) => s.nowPlaying);
  const setNowPlaying = useStore((s) => s.setNowPlaying);

  // Create the audio element when a URL is known. preload="metadata" pulls
  // just enough to read `.duration` — the user does NOT need to click play
  // first for the duration column to populate.
  useEffect(() => {
    if (!url) return;
    const a = new Audio();
    a.preload = 'metadata';
    a.src = url;

    const onMeta = () => {
      setDuration(Number.isFinite(a.duration) ? a.duration : 0);
    };
    const onTime = () => setProgress(a.currentTime);
    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
      setNowPlaying(null);
    };

    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('ended', onEnded);
    audioRef.current = a;

    return () => {
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('ended', onEnded);
      a.pause();
      audioRef.current = null;
      setPlaying(false);
    };
    // setNowPlaying from zustand is stable; safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Stop this row when another one starts playing.
  useEffect(() => {
    if (nowPlaying !== id && playing) {
      audioRef.current?.pause();
      setPlaying(false);
    }
  }, [nowPlaying, id, playing]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
      setNowPlaying(null);
    } else {
      void audioRef.current.play();
      setPlaying(true);
      setNowPlaying(id);
    }
  };

  const seek = (seconds: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = seconds;
    setProgress(seconds);
  };

  return { playing, progress, duration, toggle, seek };
}
